import { observer } from 'mobx-react-lite';
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useState,
} from 'react';

import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { PaymentMethod } from 'src/models/payments-store/payments-store';
import type { SubscriptionPlan } from 'src/models/subscription-plan/subscription-plan';

import { useAnalytics } from '@features/Analytics';
import { createAppLovinSubscriptionPlanPayload } from '@features/Analytics/utils';
import { useFeature } from '@features/FeatureToggle';
import {
  AnalyticsPaymentMethod,
  PaymentSystem,
  handleCheckoutOpenRequestWithPaymentSystem,
  usePaymentSystem,
} from '@features/Payments';

import CreditCard from '../../../../assets/icons/credit-card.svg';
import PaymentButton from '../payment-button/payment-button';

const CardPaymentButton = observer(
  ({
    subscriptionPlan,
    buttonContent = 'Debit / credit card',
    IconComponent = CreditCard,
    ...props
  }: {
    buttonContent?: ReactNode;
    IconComponent?: ReactNode;
    subscriptionPlan: SubscriptionPlan;
  } & Omit<ComponentProps<typeof PaymentButton>, 'children'>) => {
    const { paymentsStore, authStore } = useStores();
    const { user, variant } = authStore;
    const { trackPinterestEvent, trackFacebookEvent, trackAppLovin } =
      useAnalytics();
    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');
    let paymentSystem = PaymentSystem.BRAINTREE;

    const [isLoading, setIsLoading] = useState(false);
    const { contentNode, openCheckout } = usePaymentSystem({
      subscriptionPlan,
      paymentSystem,
      setIsLoading,
    });

    const handleClick = useCallback(async () => {
      if (paymentSystem != null) {
        handleCheckoutOpenRequestWithPaymentSystem({
          subscriptionPlan,
          paymentMethod: AnalyticsPaymentMethod.CARD,
          paymentSystem,
          email: user!.email,
          variant: variant,
        });
        !fbPixelSept2025Feature &&
          trackFacebookEvent({
            eventName: 'AddPaymentInfo',
            customData: {
              paymentSystem: 'braintree',
              paymentMethod: 'card',
            },
            options: {
              email: user?.email,
            },
          });
        trackPinterestEvent({ eventName: 'AddToCart' });
        trackAppLovin({
          eventName: 'add_payment_info',
          options: createAppLovinSubscriptionPlanPayload({
            subscriptionPlan,
            paymentMethod: 'card',
            paymentSystem: 'braintree',
            userEmail: user?.email,
            userId: user?.serverId,
            variant,
          }),
        });
      }

      paymentsStore.setPaymentMethod(PaymentMethod.CARD);

      await openCheckout();
    }, [
      fbPixelSept2025Feature,
      openCheckout,
      paymentSystem,
      paymentsStore,
      subscriptionPlan,
      trackAppLovin,
      trackFacebookEvent,
      trackPinterestEvent,
      user,
      variant,
    ]);

    return (
      <>
        <PaymentButton
          data-testid="pay-via-card"
          IconComponent={IconComponent}
          onClick={handleClick}
          loading={isLoading}
          {...props}
        >
          {buttonContent}
        </PaymentButton>
        {contentNode}
      </>
    );
  },
);

export default CardPaymentButton;
